import { List } from 'immutable';
import { IDispatchId, IRequestError } from '../../utils/helpers/redux.helper';
import { AccountUser } from './account-user.model';

// action payload interfaces
export interface IAccountUserPayload {
  accountUser: AccountUser;
}

export interface IUpdateNetworkPayload extends IDispatchId {
  prevAccountUser: AccountUser;
  currentAccountUser: AccountUser;
}

export interface IAddNetworkPayload extends IDispatchId, IAccountUserPayload {}

export interface IAddNetworkErrorPayload
  extends IAccountUserPayload,
    IRequestError {}

export interface IUpdateNetworkErrorPayload
  extends IUpdateNetworkPayload,
    IRequestError {}

export interface IDeleteNetworkPayload
  extends IDispatchId,
    IAccountUserPayload {}

export interface IDeleteNetworkErrorPayload
  extends IAccountUserPayload,
    IRequestError {}

export interface IUpdateAccountUsers {
  accountUsers: List<AccountUser>;
}

// action enums
export enum ActionsEnum {
  FETCH_ACCOUNT_USERS = 'FETCH_ACCOUNT_USERS',
  FETCH_ACCOUNT_USERS_SUCCESS = 'FETCH_ACCOUNT_USERS_SUCCESS',
  FETCH_ACCOUNT_USERS_ERROR = 'FETCH_ACCOUNT_USERS_ERROR',
  UPDATE_ACCOUNT_USERS = 'UPDATE_ACCOUNT_USERS',
  DELETE_ACCOUNT_USER = 'DELETE_ACCOUNT_USER',
  DELETE_ACCOUNT_USER_NETWORK = 'DELETE_ACCOUNT_USER_NETWORK',
  DELETE_ACCOUNT_USER_NETWORK_SUCCESS = 'DELETE_ACCOUNT_USER_NETWORK_SUCCESS',
  DELETE_ACCOUNT_USER_NETWORK_ERROR = 'DELETE_ACCOUNT_USER_NETWORK_ERROR',
  ADD_ACCOUNT_USER = 'ADD_ACCOUNT_USER',
  ADD_ACCOUNT_USER_NETWORK = 'ADD_ACCOUNT_USER_NETWORK',
  ADD_ACCOUNT_USER_NETWORK_SUCCESS = 'ADD_ACCOUNT_USER_NETWORK_SUCCESS',
  ADD_ACCOUNT_USER_NETWORK_ERROR = 'ADD_ACCOUNT_USER_NETWORK_ERROR',
  UPDATE_ACCOUNT_USER = 'UPDATE_ACCOUNT_USER',
  UPDATE_ACCOUNT_USER_NETWORK = 'UPDATE_ACCOUNT_USER_NETWORK',
  UPDATE_ACCOUNT_USER_NETWORK_SUCCESS = 'UPDATE_ACCOUNT_USER_NETWORK_SUCCESS',
  UPDATE_ACCOUNT_USER_NETWORK_ERROR = 'ADD_ACCOUNT_USER_NETWORK_ERROR',
}

// action definitions
export interface FetchAccountUsers {
  type: ActionsEnum.FETCH_ACCOUNT_USERS;
  payload: IDispatchId;
}

export interface FetchAccountUsersSuccess {
  type: ActionsEnum.FETCH_ACCOUNT_USERS_SUCCESS;
  payload: IDispatchId;
}

export interface FetchAccountUsersError {
  type: ActionsEnum.FETCH_ACCOUNT_USERS_ERROR;
  payload: IRequestError;
}

export interface UpdateAccountUsers {
  type: ActionsEnum.UPDATE_ACCOUNT_USERS;
  payload: IUpdateAccountUsers;
}

export interface DeleteAccountUser {
  type: ActionsEnum.DELETE_ACCOUNT_USER;
  payload: IAccountUserPayload;
}

export interface DeleteAccountUserNetwork {
  type: ActionsEnum.DELETE_ACCOUNT_USER_NETWORK;
  payload: IDeleteNetworkPayload;
}

export interface DeleteAccountUserNetworkSuccess {
  type: ActionsEnum.DELETE_ACCOUNT_USER_NETWORK_SUCCESS;
  payload: IDeleteNetworkPayload;
}

export interface DeleteAccountUserNetworkError {
  type: ActionsEnum.DELETE_ACCOUNT_USER_NETWORK_ERROR;
  payload: IDeleteNetworkErrorPayload;
}

export interface AddAccountUser {
  type: ActionsEnum.ADD_ACCOUNT_USER;
  payload: IAccountUserPayload;
}

export interface AddAccountUserNetwork {
  type: ActionsEnum.ADD_ACCOUNT_USER_NETWORK;
  payload: IAddNetworkPayload;
}

export interface AddAccountUserNetworkSuccess {
  type: ActionsEnum.ADD_ACCOUNT_USER_NETWORK_SUCCESS;
  payload: IAddNetworkPayload;
}

export interface AddAccountUserNetworkError {
  type: ActionsEnum.ADD_ACCOUNT_USER_NETWORK_ERROR;
  payload: IAddNetworkErrorPayload;
}

export interface UpdateAccountUser {
  type: ActionsEnum.UPDATE_ACCOUNT_USER;
  payload: IAccountUserPayload;
}

export interface UpdateAccountUserNetwork {
  type: ActionsEnum.UPDATE_ACCOUNT_USER_NETWORK;
  payload: IUpdateNetworkPayload;
}

export interface UpdateAccountUserNetworkSuccess {
  type: ActionsEnum.UPDATE_ACCOUNT_USER_NETWORK_SUCCESS;
  payload: IAccountUserPayload;
}

export interface UpdateAccountUserNetworkError {
  type: ActionsEnum.UPDATE_ACCOUNT_USER_NETWORK_ERROR;
  payload: IUpdateNetworkErrorPayload;
}

// action creators
export const fetchAccountUsers = (dispatchId: string): FetchAccountUsers => ({
  type: ActionsEnum.FETCH_ACCOUNT_USERS,
  payload: {
    dispatchId,
  },
});

export const fetchAccountUsersSuccess = (
  dispatchId: string
): FetchAccountUsersSuccess => ({
  type: ActionsEnum.FETCH_ACCOUNT_USERS_SUCCESS,
  payload: {
    dispatchId,
  },
});

export const fetchAccountUsersError = (
  dispatchId: string,
  error: Error
): FetchAccountUsersError => ({
  type: ActionsEnum.FETCH_ACCOUNT_USERS_ERROR,
  payload: {
    dispatchId,
    error,
  },
});

export const updateAccountUsers = (
  accountUsers: List<AccountUser>
): UpdateAccountUsers => ({
  type: ActionsEnum.UPDATE_ACCOUNT_USERS,
  payload: {
    accountUsers,
  },
});

export const deleteAccountUser = (
  accountUser: AccountUser
): DeleteAccountUser => ({
  type: ActionsEnum.DELETE_ACCOUNT_USER,
  payload: {
    accountUser,
  },
});

export const deleteAccountUserNetwork = (
  dispatchId: string,
  accountUser: AccountUser
): DeleteAccountUserNetwork => ({
  type: ActionsEnum.DELETE_ACCOUNT_USER_NETWORK,
  payload: {
    dispatchId,
    accountUser,
  },
});

export const deleteAccountUserNetworkSuccess = (
  dispatchId: string,
  accountUser: AccountUser
): DeleteAccountUserNetworkSuccess => ({
  type: ActionsEnum.DELETE_ACCOUNT_USER_NETWORK_SUCCESS,
  payload: {
    dispatchId,
    accountUser,
  },
});

export const deleteAccountUserNetworkError = (
  dispatchId: string,
  accountUser: AccountUser,
  error: Error
): DeleteAccountUserNetworkError => ({
  type: ActionsEnum.DELETE_ACCOUNT_USER_NETWORK_ERROR,
  payload: {
    dispatchId,
    accountUser,
    error,
  },
});

export const addAccountUser = (accountUser: AccountUser): AddAccountUser => ({
  type: ActionsEnum.ADD_ACCOUNT_USER,
  payload: {
    accountUser,
  },
});

export const addAccountUserNetwork = (
  dispatchId: string,
  accountUser: AccountUser
): AddAccountUserNetwork => ({
  type: ActionsEnum.ADD_ACCOUNT_USER_NETWORK,
  payload: {
    dispatchId,
    accountUser,
  },
});

export const addAccountUserNetworkSuccess = (
  dispatchId: string,
  accountUser: AccountUser
): AddAccountUserNetworkSuccess => ({
  type: ActionsEnum.ADD_ACCOUNT_USER_NETWORK_SUCCESS,
  payload: {
    dispatchId,
    accountUser,
  },
});

export const addAccountUserNetworkError = (
  dispatchId: string,
  accountUser: AccountUser,
  error: Error
): AddAccountUserNetworkError => ({
  type: ActionsEnum.ADD_ACCOUNT_USER_NETWORK_ERROR,
  payload: {
    dispatchId,
    accountUser,
    error,
  },
});

export const updateAccountUser = (
  accountUser: AccountUser
): UpdateAccountUser => ({
  type: ActionsEnum.UPDATE_ACCOUNT_USER,
  payload: {
    accountUser,
  },
});

export const updateAccountUserNetwork = (
  dispatchId: string,
  prevAccountUser: AccountUser,
  currentAccountUser: AccountUser
): UpdateAccountUserNetwork => ({
  type: ActionsEnum.UPDATE_ACCOUNT_USER_NETWORK,
  payload: {
    dispatchId,
    prevAccountUser,
    currentAccountUser,
  },
});

export const updateAccountUserNetworkSuccess = (
  dispatchId: string,
  accountUser: AccountUser
): UpdateAccountUserNetworkSuccess => ({
  type: ActionsEnum.UPDATE_ACCOUNT_USER_NETWORK_SUCCESS,
  payload: {
    accountUser,
  },
});

export const updateAccountUserNetworkError = (
  dispatchId: string,
  prevAccountUser: AccountUser,
  currentAccountUser: AccountUser,
  error: Error
): UpdateAccountUserNetworkError => ({
  type: ActionsEnum.UPDATE_ACCOUNT_USER_NETWORK_ERROR,
  payload: {
    dispatchId,
    prevAccountUser,
    currentAccountUser,
    error,
  },
});
